import * as React from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import CodeIcon from "@mui/icons-material/Code";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useAuth0 } from "@auth0/auth0-react";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8787";

export default function WebsitesList({
  websites,
  loading,
  error,
  onRefresh,
  onViewAllJson,
  onViewWebsiteDetails,
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [expandedId, setExpandedId] = React.useState(null);
  const [expandedData, setExpandedData] = React.useState(null);
  const [expandedLoading, setExpandedLoading] = React.useState(false);

  const fetchWebsiteDetails = React.useCallback(async (websiteId) => {
    setExpandedLoading(true);
    setExpandedData(null);
    try {
      const token = await getAccessTokenSilently().catch(() => null);
      const response = await fetch(
        `${API_BASE_URL}/admin/squarespace/websites/${encodeURIComponent(websiteId)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      const data = await response.json();
      if (response.ok) {
        setExpandedData(data.data || data);
      } else {
        setExpandedData({ error: data.message || data.error || "Failed to load" });
      }
    } catch (err) {
      setExpandedData({ error: err.message || "Failed to load" });
    } finally {
      setExpandedLoading(false);
    }
  }, [getAccessTokenSilently]);

  const handleRowClick = (website) => {
    const id = website?.id || website?.websiteId;
    if (!id) return;
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedData(null);
    } else {
      setExpandedId(id);
      fetchWebsiteDetails(id);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" fontWeight="bold">
          Websites
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CodeIcon />}
            onClick={onViewAllJson}
          >
            View JSON
          </Button>
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : websites.length === 0 ? (
        <Alert severity="info">No websites found for this customer.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Website ID</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>sqspWebsiteId</TableCell>
                <TableCell>URL</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {websites.map((website) => {
                const rowId = website.id || website.websiteId;
                const isExpanded = expandedId === rowId;
                const rowData = isExpanded ? expandedData : null;
                return (
                  <React.Fragment key={rowId}>
                    <TableRow
                      hover
                      onClick={() => handleRowClick(website)}
                      sx={{ cursor: "pointer", "&:hover": { backgroundColor: "action.hover" } }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {rowId || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {website.title || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {website.sqspWebsiteId || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: "0.75rem" }}>
                          {website.url || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        {onViewWebsiteDetails && (
                          <IconButton
                            size="small"
                            onClick={() => onViewWebsiteDetails(website)}
                            aria-label="View full website details"
                          >
                            <CodeIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ py: 2, backgroundColor: (theme) => theme.palette.mode === "dark" ? theme.palette.grey[900] : theme.palette.grey[50] }}>
                          {expandedLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                              <CircularProgress size={24} />
                            </Box>
                          ) : rowData?.error ? (
                            <Alert severity="error">{rowData.error}</Alert>
                          ) : rowData ? (
                            <Box>
                              {rowData.title && (
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                  {rowData.title}
                                </Typography>
                              )}
                              <Box sx={{ display: "flex", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
                                {rowData.url && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<OpenInNewIcon />}
                                    href={rowData.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Open URL
                                  </Button>
                                )}
                                {rowData.websiteManagementUrl && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<OpenInNewIcon />}
                                    href={rowData.websiteManagementUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Open Config
                                  </Button>
                                )}
                                {!rowData.url && !rowData.websiteManagementUrl && (
                                  <Typography variant="body2" color="text.secondary">
                                    No URL or config link available
                                  </Typography>
                                )}
                              </Box>
                              <Box
                                component="pre"
                                sx={{
                                  p: 1.5,
                                  borderRadius: 1,
                                  backgroundColor: (theme) => theme.palette.mode === "dark" ? theme.palette.grey[800] : theme.palette.grey[100],
                                  fontSize: "0.75rem",
                                  fontFamily: "monospace",
                                  overflow: "auto",
                                  maxHeight: 200,
                                  margin: 0,
                                }}
                              >
                                {JSON.stringify(rowData, null, 2)}
                              </Box>
                            </Box>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
